import { Alert, AlertDescription } from "@/components/ui/alert";

import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "../components/top-bar/Logo";
import apiClient from "../utils/apiClient";
import { useAuthStore } from "../stores/authStore";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [motDePasseVisible, setMotDePasseVisible] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation locale
    if (!email || !password || !confirmPassword) {
      setError("Tous les champs sont requis");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post("/auth/register-admin", {
        email,
        password,
      });

      // Récupérer les données de la réponse
      const { token, refreshToken, user } = response.data;

      // Connecter automatiquement l'utilisateur
      login(token, refreshToken, user);

      // Redirection vers la page d'accueil après inscription et connexion réussies
      navigate("/");
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Erreur lors de la création du compte",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSignup} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="admin@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative">
              <Input
                type={motDePasseVisible ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Au moins 8 caractères"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full cursor-pointer px-3 text-muted-foreground hover:bg-transparent"
                onClick={() => setMotDePasseVisible((visible) => !visible)}
                aria-label={motDePasseVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                disabled={loading}
              >
                {motDePasseVisible ? <EyeOff /> : <Eye />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <div className="relative">
              <Input
                type={confirmationVisible ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Répétez le mot de passe"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full cursor-pointer px-3 text-muted-foreground hover:bg-transparent"
                onClick={() => setConfirmationVisible((visible) => !visible)}
                aria-label={confirmationVisible ? "Masquer la confirmation du mot de passe" : "Afficher la confirmation du mot de passe"}
                disabled={loading}
              >
                {confirmationVisible ? <EyeOff /> : <Eye />}
              </Button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-6">
            {loading ? "Création en cours..." : "Créer compte"}
          </Button>
        </form>
      </div>
    </div>
  );
}
